import { Injectable } from '@angular/core';
import {
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpInterceptor,
	HttpErrorResponse,
	HttpResponse,
} from '@angular/common/http';
import { finalize, Observable, tap } from 'rxjs';
import { UtilsService } from '../services/utils/utils.service';
import {
	CmvHttpBasicResponse,
	CmvHttpEntidadResponse,
} from '../models/apis/entities';
import { TYPE_TOAST } from '../models/enumeraciones';
import { ReturnOfInvestmenResponse } from '../models/apis/investment.model';
import { MonitoringService } from '../services/monitoring/monitoring.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
	readonly validCodes = ['200', '201', '400', '401', '404'];
	readonly validationCodes = ['407'];
	readonly excludedErrorCodes = ['400', '401', '404'];

	constructor(
		private utilService: UtilsService,
		private monitoringService: MonitoringService
	) {}

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		let response: HttpResponse<
			| CmvHttpEntidadResponse<any>
			| ReturnOfInvestmenResponse
			| Array<CmvHttpBasicResponse>
		>;
		let errorResponse: HttpErrorResponse;
		const url = request.url;
		const shouldSkipErrorToast = this.shouldSkipErrorToast(url);

		const ERROR_MESSAGE = 'Ocurrio un error inesperado intente más tarde';
		return next.handle(request).pipe(
			tap({
				// Angular emite primero el evento `Sent`, que no trae `status` ni
				// `body`. Guardarlo hacía que el `finalize` de una petición fallida lo
				// tratara como respuesta y reventara en `response.status.toString()`;
				// ese TypeError, lanzado durante la baja de la suscripción, sustituía
				// al error real por un `UnsubscriptionError` de RxJS y era ese texto el
				// que terminaba pintado en la pantalla de rechazo.
				next: (event: HttpEvent<CmvHttpEntidadResponse<any>>) => {
					if (event instanceof HttpResponse) {
						response = event;
					}
				},
				error: error => {
					errorResponse = error;
					if (this.excludedErrorCodes.includes(this.statusOf(errorResponse))) {
						errorResponse['body'] = errorResponse.error;
					} else {
						this.monitoringService.captureError(errorResponse);
						if (!shouldSkipErrorToast) {
							this.utilService.ShowToast(
								ERROR_MESSAGE,
								TYPE_TOAST.ERROR,
								10000
							);
						}
					}
				},
			}),
			finalize(() => {
				const timeOut = 10000;
				if (
					errorResponse &&
					response &&
					!this.excludedErrorCodes.includes(response.status.toString())
				) {
					const { message, ok, statusText, status, name } = errorResponse;
					console.log(
						'🚀 ~ file: error.interceptor.ts:37 ~ ErrorInterceptor ~ finalize ~ {message, ok, statusText, status, name}:',
						{ message, ok, statusText, status, name }
					);
					this.monitoringService.captureError(errorResponse);

					if (!shouldSkipErrorToast) {
						this.utilService.ShowToast(
							ERROR_MESSAGE,
							TYPE_TOAST.ERROR,
							timeOut
						);
					}
				}

				if (!response || this.excludedErrorCodes.includes(response.status.toString()))
					return;

				const responseBody = response.body;

				if ('CalcularRendimientoInversionResult' in responseBody) {
					this.handleCalcularRendimientoInversionErrors(responseBody, timeOut);
					return;
				}
				if (
					'ObtenerProductosInversionResult' in responseBody ||
					'solicitudAprobada' in responseBody
				) {
					return;
				}

				if (
					'Codigo' in responseBody &&
					'Mensaje' in responseBody &&
					'Entidad' in responseBody
				) {
					this.handleCmvEntidadResponse(
						responseBody as CmvHttpEntidadResponse<any>,
						ERROR_MESSAGE,
						timeOut
					);
				}
			})
		);
	}

	/**
	 * No todo lo que llega por el canal de error es un `HttpErrorResponse`: los
	 * servicios lanzan `new Error(response.mensaje)` desde sus `map` cuando el
	 * backend responde 200 con `estado: false`. Esos errores no traen `status`, y
	 * leerlo a ciegas revienta con "Cannot read properties of undefined (reading
	 * 'toString')" **dentro del interceptor**, lo que reemplaza el error original
	 * por un `UnsubscriptionError` de RxJS: el socio acaba viendo ese texto en la
	 * pantalla de rechazo en lugar del motivo real.
	 */
	private statusOf(error: unknown): string {
		const status = (error as HttpErrorResponse)?.status;
		return status === undefined || status === null ? '' : status.toString();
	}

	private handleCmvEntidadResponse(
		responseBody: CmvHttpEntidadResponse<any>,
		ERROR_MESSAGE: string,
		timeOut: number
	) {
		const { Codigo: code, Mensaje: internalMessage } =
			responseBody as CmvHttpEntidadResponse<any>;
		if (!this.validCodes.includes(code)) {
			const messageForInternalCode =
				code === '500' ? ERROR_MESSAGE : internalMessage;
			this.monitoringService.captureError(new Error(messageForInternalCode), {
				code,
			});
			if (!this.validationCodes.includes(code)) {
				this.utilService.ShowToast(
					messageForInternalCode,
					TYPE_TOAST.ERROR,
					timeOut
				);
			}
		}
	}

	private handleCalcularRendimientoInversionErrors(
		responseBody: ReturnOfInvestmenResponse,
		timeOut: number
	) {
		if (!responseBody.CalcularRendimientoInversionResult.Estado) {
			const { Mensaje: message, Codigo: internalCode } =
				responseBody.CalcularRendimientoInversionResult;
			this.monitoringService.captureError(new Error(message), {
				internalCode,
			});
			this.utilService.ShowToast(message, TYPE_TOAST.ERROR, timeOut);
		}
	}

	private shouldSkipErrorToast(requestUrl: string): boolean {
		if (requestUrl?.includes('logDigitalAnalitica')) return true;
		if (requestUrl?.includes('ofertasMotor')) return true;
		if (requestUrl?.includes('initialInformation')) return true;
		return false;
	}
}
